import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const STATUS_TYPES = {
  PENDING: "Afventer",
  ACTIVE: "Aktiv",
  CLOSED: "Lukket"
};

export const useDataExchange = () => {
  const [aktivLonkorsel, setAktivLonkorsel] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [earliestAllowedDate, setEarliestAllowedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');
  const [kundeData, setKundeData] = useState(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [allPayRuns, setAllPayRuns] = useState([]);
  const [inputStatus, setInputStatus] = useState({
    status: 'open',
    deadline: new Date().toISOString().split('T')[0],
    approvalMaterialDeadline: null,
    approvalDeadline: null,
    outputDeadline: null
  });

  // State for filer og data
  const [salaryItems, setSalaryItems] = useState([]);
  const [employeeChanges, setEmployeeChanges] = useState([]);
  const [inputFiles, setInputFiles] = useState([]);
  const [approvalFiles, setApprovalFiles] = useState([]);
  const [outputFiles, setOutputFiles] = useState([]);

  // Hent initial data
  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [payRunsResponse, earliestDateResponse] = await Promise.all([
        api.get('/pay-runs'),
        api.get('/earliest-allowed-date')
      ]);

      if (!Array.isArray(payRunsResponse)) {
        throw new Error('Ugyldigt svar fra serveren');
      }

      setAllPayRuns(payRunsResponse);
      const aktiv = payRunsResponse.find(run => run.status === STATUS_TYPES.ACTIVE);
      
      if (earliestDateResponse?.month && earliestDateResponse?.year) {
        const earliestDate = {
          month: earliestDateResponse.month,
          year: earliestDateResponse.year
        };
        setEarliestAllowedDate(earliestDate);
        localStorage.setItem('earliestAllowedDate', JSON.stringify(earliestDate));
      }
      
      if (aktiv) {
        setAktivLonkorsel(aktiv);
        setSelectedMonth(aktiv.month);
        setSelectedYear(aktiv.year);
      } else {
        const now = new Date();
        setSelectedMonth(now.getMonth() + 1);
        setSelectedYear(Math.max(now.getFullYear(), 2024));
      }
      setInitialized(true);
    } catch (error) {
      setError('Der opstod en fejl ved hentning af data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Hent filer
  const fetchFiles = useCallback(async () => {
    if (!initialized || !selectedMonth || !selectedYear) return;

    try {
      setIsLoadingFiles(true);
      const [inputFiles, approvalFiles, outputFiles] = await Promise.all([
        api.get(`/files/input?month=${selectedMonth}&year=${selectedYear}`),
        api.get(`/files/approval?month=${selectedMonth}&year=${selectedYear}`),
        api.get(`/files/output?month=${selectedMonth}&year=${selectedYear}`)
      ]);

      setInputFiles(inputFiles || []);
      setApprovalFiles(approvalFiles || []);
      setOutputFiles(outputFiles || []);
    } catch (error) {
      setError('Der opstod en fejl ved hentning af filer');
    } finally {
      setIsLoadingFiles(false);
    }
  }, [selectedMonth, selectedYear, initialized]);

  // Hent salary data
  const fetchSalaryData = useCallback(async () => {
    if (!aktivLonkorsel) return;
    
    try {
      const [items, changes] = await Promise.all([
        api.get(`/pay-runs/${aktivLonkorsel.id}/salary-items`),
        api.get('/employee-changes')
      ]);

      setSalaryItems(items);
      setEmployeeChanges(changes);
    } catch (err) {
      setError('Der opstod en fejl ved hentning af løndata: ' + err.message);
    }
  }, [aktivLonkorsel]);

  // Hent kundeoplysninger
  const fetchKundeData = useCallback(async () => {
    try {
      const data = await api.get('/info');
      setKundeData(data);
    } catch (err) {
      setError('Der opstod en fejl ved hentning af kundeoplysninger');
    }
  }, []);

  // Effekter
  useEffect(() => {
    // Prøv at hente tidligste dato fra localStorage først
    const savedEarliestDate = localStorage.getItem('earliestAllowedDate');
    if (savedEarliestDate) {
      try {
        const parsed = JSON.parse(savedEarliestDate);
        if (parsed?.month && parsed?.year) {
          setEarliestAllowedDate(parsed);
        }
      } catch (e) {
        // Ignorer fejl ved parsing af cached data
      }
    }

    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    fetchSalaryData();
  }, [fetchSalaryData]);

  useEffect(() => {
    fetchKundeData();
  }, [fetchKundeData]);

  // Opdater input status når måned/år ændres
  useEffect(() => {
    if (!initialized) return;

    const payRun = allPayRuns.find(run => 
      run.month === selectedMonth &&
      run.year === selectedYear
    );

    if (payRun) {
      setInputStatus({
        id: payRun.id,
        status: payRun.status,
        payroll_status: payRun.payroll_status,
        deadline: payRun.input_deadline,
        approvalMaterialDeadline: payRun.approval_material_deadline,
        approvalDeadline: payRun.approval_deadline,
        outputDeadline: payRun.output_deadline
      });
    } else {
      setInputStatus(null);
    }
  }, [selectedMonth, selectedYear, initialized, allPayRuns]);

  // Hjælpefunktioner
  const isUploadAllowed = useCallback(() => {
    if (!selectedMonth || !selectedYear || !allPayRuns.length) return false;
    
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.role === 'admin' || user.role === 'consultant') return true;
    
    const payRun = allPayRuns.find(run => 
      run.month === selectedMonth &&
      run.year === selectedYear
    );

    return !payRun || payRun.status !== STATUS_TYPES.CLOSED;
  }, [selectedMonth, selectedYear, allPayRuns]);

  const isEditingAllowed = useCallback(() => {
    if (!selectedMonth || !selectedYear || !allPayRuns.length) return false;
    
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.role === 'admin' || user.role === 'consultant') return true;
    
    const payRun = allPayRuns.find(run => 
      run.month === selectedMonth &&
      run.year === selectedYear
    );

    return !payRun || payRun.status !== STATUS_TYPES.CLOSED;
  }, [selectedMonth, selectedYear, allPayRuns]);

  return {
    // State
    aktivLonkorsel,
    selectedMonth,
    selectedYear,
    earliestAllowedDate,
    loading,
    initialized,
    error,
    kundeData,
    isLoadingFiles,
    inputStatus,
    salaryItems,
    employeeChanges,
    inputFiles,
    approvalFiles,
    outputFiles,
    
    // Setters
    setSelectedMonth,
    setSelectedYear,
    setSalaryItems,
    setEmployeeChanges,
    setInputFiles,
    setApprovalFiles,
    setOutputFiles,
    setInputStatus,
    
    // Hjælpefunktioner
    isUploadAllowed,
    isEditingAllowed
  };
}; 